import './textAreaEditor.scss';

import { hooks } from '@approvalmax/utils';
import { FocusEvent, forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import bemFactory from 'react-bem-factory';
import TextareaAutosize from 'react-textarea-autosize';

import { FieldContext } from '../../field/Field';
import { getDisabled } from '../../helpers';
import { BaseEditorProps } from '../EditorBase';
import { messages } from './TextAreaEditor.messages';

export const ALL_TEXT_AREA_THEMES = ['form', 'transparent'] as const;
export type TextAreaTheme = (typeof ALL_TEXT_AREA_THEMES)[number];

export const ALL_TEXT_AREA_SIZES = [
    'auto' /** see minHeight, maxHeight options */,
    'fixed' /** see height option */,
] as const;
export type TextAreaSize = (typeof ALL_TEXT_AREA_SIZES)[number];

interface Props extends BaseEditorProps<string> {
    placeholder?: string;
    maxLength?: number;
    size?: TextAreaSize;
    theme?: TextAreaTheme;
    className?: string;
    height?: number;
    minHeight?: number;
    maxHeight?: number;
    onCtrlEnter?: () => void;
    changeOnBlur?: boolean;
    onFocus?: (e?: FocusEvent<HTMLTextAreaElement>) => void;
    title?: string;
    /** row height in pixels */
    rowHeight?: number;
}

export interface TextAreaEditorElement {
    focus(): void;
    blur(): void;
}

const TextAreaEditor = forwardRef<TextAreaEditorElement, Props>((props, ref) => {
    const {
        className,
        theme = 'form',
        size = 'auto',
        invalid,
        qa,
        placeholder = messages.defaultPlaceholder,
        maxLength,
        height = 4,
        minHeight = 2,
        maxHeight = 20,
        changeOnBlur,
        focusOnMount,
        title,
        rowHeight,
    } = props;
    const bem = bemFactory.block('form-text-area-editor').themed(theme);

    const disabled = getDisabled(props.disabled, props.command);

    const [stateValue, setStateValue] = useState(props.value || '');
    const value = changeOnBlur ? stateValue : props.value || '';

    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && props.onCtrlEnter) {
            props.onCtrlEnter();
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (changeOnBlur) {
            setStateValue(e.target.value);
        } else {
            props.onChange(e.target.value);
        }
    };

    const onFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
        if (props.onFocus) {
            props.onFocus(e);
        }
    };

    const onBlur = () => {
        if (changeOnBlur) {
            props.onChange(stateValue);
        }

        if (props.onBlur) {
            props.onBlur();
        }
    };

    const { fieldId } = useContext(FieldContext);

    hooks.useFocusOnMount(focusOnMount, textAreaRef);

    // Update the stateValue if props.value changes
    useEffect(() => {
        if (changeOnBlur) {
            setStateValue(props.value || '');
        }
    }, [changeOnBlur, props.value]);

    useImperativeHandle(
        ref,
        () => ({
            focus() {
                textAreaRef.current?.focus();
            },
            blur() {
                textAreaRef.current?.blur();
            },
        }),
        [textAreaRef]
    );

    const textAreaProps = {
        className: bem.add(className)(null, {
            invalid,
        }),
        value,
        onKeyDown,
        onChange,
        onFocus,
        onBlur,
        disabled,
        placeholder,
        maxLength,
        title,
        rows: size === 'auto' ? minHeight : height,
    };

    const fixedHeightStyles = useMemo(() => {
        if (size !== 'fixed' || !rowHeight) {
            return undefined;
        }

        return {
            height: `${rowHeight * height}px`,
        };
    }, [size, height, rowHeight]);

    /**
     * If the size if fixed and rowHeight is set
     * than we can use pure textarea without autosize
     * It will increase perfomance for example in large tables
     * in requests with many lines
     */
    if (fixedHeightStyles) {
        return <textarea id={fieldId} ref={textAreaRef} data-qa={qa} {...textAreaProps} style={fixedHeightStyles} />;
    }

    return (
        <TextareaAutosize
            minRows={size === 'auto' ? minHeight : height}
            maxRows={size === 'auto' ? maxHeight : height}
            id={fieldId}
            ref={textAreaRef}
            data-qa={qa}
            {...textAreaProps}
        />
    );
});

export default TextAreaEditor;
